import React from 'react';
import { message, notification } from 'antd';
import { IApiError, isApiError, ApiErrorHandler, isCancelledRequestError } from './apiTypes';

// Default durations in seconds
const SUCCESS_DURATION = 2;
const ERROR_DURATION = 10;
const WARNING_DURATION = 3;
const INFO_DURATION = 3;

// Legacy error response type for backward compatibility
export interface ErrorResponse {
  status?: number;
  statusText?: string;
  response?: {
    status: number;
    statusText: string;
    data?: any;
  };
  message?: string;
}

export const toastSuccess = (content: string) => 
  message.success(content, SUCCESS_DURATION);

export const toastError = (content: string, error?: any) => {
  if (isCancelledRequestError(error)) {
    return;
  }

  console.error(content, error);
  
  if (!error) {
    message.error(content, ERROR_DURATION);
    return;
  }

  const isNetworkError = error.statusCode === 0 && error.message === 'Network Error';
  if (isNetworkError) {
    message.error('Kunde inte ansluta till servern. Kontrollera din internetanslutning.', ERROR_DURATION);
    return;
  }

  // Handle standardized API errors
  if (isApiError(error)) {
    const userMessage = ApiErrorHandler.getUserFriendlyMessage(error);
    const displayMessage = `${content}: ${userMessage}`;
    message.error(displayMessage, ERROR_DURATION);
    return;
  }

  // Handle axios errors by converting them to API errors first
  if (error.response || error.request || error.config) {
    // Check if it's a blob error that needs async handling
    if (error.response?.data instanceof Blob) {
      // For blob errors, we'll handle them asynchronously
      handleBlobError(content, error);
      return;
    }
    
    const apiError = ApiErrorHandler.parseAxiosError(error);
    const userMessage = ApiErrorHandler.getUserFriendlyMessage(apiError);
    const displayMessage = `${content}: ${userMessage}`;
    
    // Log additional details for debugging
    console.error('Converted API Error Details:', {
      url: apiError.url,
      statusCode: apiError.statusCode,
      correlationId: apiError.correlationId,
      timestamp: apiError.timestamp
    });
    
    message.error(displayMessage, ERROR_DURATION);
    return;
  }

  // Handle legacy axios error response format (backward compatibility)
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;
    const msg = `${content} (${status}: ${data})`;
    message.error(msg, ERROR_DURATION);
    return;
  }

  // Handle custom ErrorResponse (backward compatibility)
  if (error.status && error.statusText) {
    const msg = `${content} (${error.status}: ${error.statusText})`;
    message.error(msg, ERROR_DURATION);
    return;
  }

  // Default error message
  message.error(`${content}: ${error.message || 'Ett oväntat fel uppstod'}`, ERROR_DURATION);
};

/**
 * Async error handler for blob responses
 * @param content - The context message
 * @param error - The error object with blob data
 */
const handleBlobError = async (content: string, error: any) => {
  try {
    const apiError = await ApiErrorHandler.parseBlobError(error);
    const userMessage = ApiErrorHandler.getUserFriendlyMessage(apiError);
    const displayMessage = `${content}: ${userMessage}`;
    
    // Log additional details for debugging
    console.error('Blob API Error Details:', {
      url: apiError.url,
      statusCode: apiError.statusCode,
      correlationId: apiError.correlationId,
      timestamp: apiError.timestamp
    });
    
    message.error(displayMessage, ERROR_DURATION);
  } catch (blobError) {
    console.error('Error handling blob error:', blobError);
    message.error(`${content}: Ett fel uppstod vid filhantering`, ERROR_DURATION);
  }
};

/**
 * Async version of toastError for handling blob errors
 * @param content - The context message
 * @param error - The error object
 */
export const toastErrorAsync = async (content: string, error?: any) => {
  if (isCancelledRequestError(error)) {
    return;
  }

  if (!error) {
    message.error(content, ERROR_DURATION);
    return;
  }

  // Handle blob errors asynchronously
  if (error.response?.data instanceof Blob) {
    await handleBlobError(content, error);
    return;
  }

  // For non-blob errors, use the regular handler
  toastError(content, error);
};

/**
 * Enhanced error handler that can also handle authentication redirects
 * @param content - The context message
 * @param error - The error object
 * @param onAuthError - Optional callback for authentication errors
 */
export const toastErrorWithAuth = (
  content: string, 
  error?: any, 
  onAuthError?: () => void
) => {
  if (isCancelledRequestError(error)) {
    return;
  }

  if (error && (isApiError(error) || error.response || error.request || error.config)) {
    const apiError = isApiError(error) ? error : ApiErrorHandler.parseAxiosError(error);
    
    // Handle authentication errors
    if (ApiErrorHandler.shouldRedirectToLogin(apiError) && onAuthError) {
      onAuthError();
      return;
    }
  }
  
  // Use regular error handling for all other cases
  toastError(content, error);
};

export const toastWarning = (content: string) => 
  message.warning(content, WARNING_DURATION);

export const toastInfo = (content: string) => 
  message.info(content, INFO_DURATION);

/**
 * Utility function to extract correlation ID from error for logging
 * @param error - The error object
 * @returns string | undefined - The correlation ID if available
 */
export const getErrorCorrelationId = (error: any): string | undefined => {
  if (isApiError(error)) {
    return error.correlationId;
  }
  
  return undefined;
};

/**
 * Shows a prominent error notification that requires user interaction to close
 * This is better than toast messages for important errors that users need to notice
 * @param title - The error title/context
 * @param error - The error object (IApiError or axios error)
 * @param options - Optional configuration for the notification
 */
export const showErrorNotification = (
  title: string,
  error?: any,
  options?: {
    description?: string;
    duration?: number; // 0 = persistent until closed
    showDetails?: boolean; // Show correlation ID and technical details
  }
) => {
  if (isCancelledRequestError(error)) {
    return;
  }

  const showDetails = options?.showDetails ?? true;
  const duration = options?.duration ?? 0; // 0 means persistent until closed

  let apiError: IApiError | null = null;
  let userMessage = options?.description || 'Ett fel uppstod';

  // Convert error to IApiError format if needed
  if (error) {
    if (isApiError(error)) {
      apiError = error;
    } else if (error.response || error.request || error.config) {
      // Handle blob errors
      if (error.response?.data instanceof Blob) {
        // For blob errors, show a generic message and log details
        console.error('Blob error detected:', error);
        userMessage = 'Ett fel uppstod vid filhantering';
      } else {
        apiError = ApiErrorHandler.parseAxiosError(error);
      }
    }
  }

  // Get user-friendly message
  if (apiError) {
    // Let explicit descriptions override automatic message mapping
    userMessage = options?.description || ApiErrorHandler.getUserFriendlyMessage(apiError);
  } else if (error?.message && !isApiError(error)) {
    userMessage = error.message;
  }

  // Generate key early so we can use it in the details element for reliable lookup
  const notificationKey = `error-${Date.now()}-${Math.random()}`;

  // Build description with optional technical details
  let description: string | React.ReactNode = userMessage;
  let hasDetails = false;
  
  if (showDetails && apiError) {
    const details: string[] = [];
    
    // Backend base URL
    if (apiError.backendSource) {
      let backendBaseUrl = '';
      if (apiError.url) {
        try {
          const url = new URL(apiError.url);
          backendBaseUrl = `${url.protocol}//${url.host}`;
        } catch {
          // If URL parsing fails, try to extract base URL from url
          const match = apiError.url.match(/^(https?:\/\/[^/]+)/);
          if (match) {
            backendBaseUrl = match[1];
          }
        }
      }
      
      // If we couldn't extract from URL, use backendSource to determine base URL
      if (!backendBaseUrl) {
        if (apiError.backendSource === 'dotnet') {
          backendBaseUrl = process.env.REACT_APP_BACKEND_URI || 'Unknown';
        } else if (apiError.backendSource === 'puppeteer') {
          backendBaseUrl = process.env.REACT_APP_PDF_GENERATOR_URL || 'Unknown';
        } else {
          backendBaseUrl = 'Unknown';
        }
      }
      
      details.push(`API: ${backendBaseUrl}`);
    }
    
    // URL
    if (apiError.url) {
      details.push(`URL: ${apiError.url}`);
    }
    
    if (apiError.statusCode !== undefined) {
      details.push(`Statuskod: ${apiError.statusCode}`);
    }
    
    if (apiError.timestamp) {
      const date = new Date(apiError.timestamp);
      details.push(`Tidpunkt: ${date.toLocaleString('sv-SE')}`);
    }
    
    // Correlation ID last
    if (apiError.correlationId) {
      details.push(`Korrelations-ID: ${apiError.correlationId}`);
    }

    if (details.length > 0) {
      hasDetails = true;
      description = React.createElement(
        React.Fragment,
        null,
        React.createElement('div', { style: { marginBottom: '8px' } }, userMessage),
        React.createElement(
          'details',
          { 
            style: { marginTop: '8px', fontSize: '12px', color: '#666' },
            'data-notification-key': notificationKey,
          },
          React.createElement(
            'summary',
            { style: { cursor: 'pointer', userSelect: 'none' } },
            'Tekniska detaljer'
          ),
          React.createElement(
            'div',
            { style: { marginTop: '8px', fontFamily: 'monospace' } },
            details.map((detail, index) =>
              React.createElement('div', { key: index }, detail)
            )
          )
        )
      );
    }
  }

  // Show notification – overflow:visible so expanded "Tekniska detaljer" isn't clipped (Ant Design uses overflow:hidden on notice)
  notification.error({
    message: title,
    description: description,
    duration: duration,
    placement: 'topRight',
    key: notificationKey,
    style: {
      width: 400,
      overflow: 'visible',
    },
    onClose: () => {
      // Cleanup is done via MutationObserver when node is removed
    }
  });

  // Set up toggle listener and ResizeObserver so stacked notifications expand height correctly
  if (hasDetails) {
    setTimeout(() => {
      const detailsElement = document.querySelector<HTMLDetailsElement>(`details[data-notification-key="${notificationKey}"]`);
      if (!detailsElement) return;

      const notice = detailsElement.closest('.ant-notification-notice') as HTMLElement | null;
      const noticeWrapper = notice?.parentElement as HTMLElement | null;
      if (!notice || !noticeWrapper) return;

      const applyExpandedHeight = () => {
        requestAnimationFrame(() => {
          if (!notice.isConnected) return;
          // Allow content to be visible (Ant Design notice has overflow:hidden)
          notice.style.overflow = 'visible';
          notice.style.height = 'auto';
          notice.style.maxHeight = 'none';
          // Wrapper gets fixed height from stack – override so this notice can grow
          noticeWrapper.style.height = `${notice.offsetHeight}px`;
        });
      };

      const handleToggle = () => {
        // Use double rAF so layout is updated after details open/close
        requestAnimationFrame(() => {
          requestAnimationFrame(applyExpandedHeight);
        });
      };

      detailsElement.addEventListener('toggle', handleToggle);

      // When content size changes (e.g. details expand), keep wrapper height in sync (works for stacked notices)
      const resizeObserver = new ResizeObserver(() => {
        handleToggle();
      });
      resizeObserver.observe(notice);

      const cleanup = () => {
        detailsElement.removeEventListener('toggle', handleToggle);
        resizeObserver.disconnect();
      };

      const observer = new MutationObserver(() => {
        if (!document.contains(detailsElement)) {
          cleanup();
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }, 100);
  }

  // Also log to console for debugging
  if (apiError) {
    console.error('Error Notification:', {
      title,
      url: apiError.url,
      statusCode: apiError.statusCode,
      message: apiError.message,
      correlationId: apiError.correlationId,
      timestamp: apiError.timestamp
    });
  } else {
    console.error('Error Notification:', { title, error });
  }
};

/**
 * Async version of showErrorNotification for handling blob errors
 * @param title - The error title/context
 * @param error - The error object
 * @param options - Optional configuration for the notification
 */
export const showErrorNotificationAsync = async (
  title: string,
  error?: any,
  options?: {
    description?: string;
    duration?: number;
    showDetails?: boolean;
  }
) => {
  if (isCancelledRequestError(error)) {
    return;
  }

  // Handle blob errors asynchronously
  if (error?.response?.data instanceof Blob) {
    try {
      const apiError = await ApiErrorHandler.parseBlobError(error);
      showErrorNotification(title, apiError, options);
      return;
    } catch (blobError) {
      console.error('Error handling blob error:', blobError);
      showErrorNotification(title, { message: 'Ett fel uppstod vid filhantering' }, options);
      return;
    }
  }

  // For non-blob errors, use the regular handler
  showErrorNotification(title, error, options);
};